// Hotel Lifecycle Status Types
// Phase 18b.3 — `OFFBOARDED` added for the new Hotel Lifecycle Dashboard card
// "Offboarded" without removing legacy statuses.
export const LIFECYCLE_STATUS = {
  ONBOARDING: 'onboarding',
  ONBOARDED: 'onboarded',
  OFFBOARDING: 'offboarding',
  OFFBOARDED: 'offboarded',
};

export const LIFECYCLE_STATUS_LABELS = {
  [LIFECYCLE_STATUS.ONBOARDING]: 'Onboarding',
  [LIFECYCLE_STATUS.ONBOARDED]: 'Onboarded',
  [LIFECYCLE_STATUS.OFFBOARDING]: 'Offboarding',
  [LIFECYCLE_STATUS.OFFBOARDED]: 'Offboarded',
};

export const LIFECYCLE_STATUS_COLORS = {
  [LIFECYCLE_STATUS.ONBOARDING]: { bg: 'bg-blue-100', text: 'text-blue-600', border: 'border-blue-300' },
  [LIFECYCLE_STATUS.ONBOARDED]: { bg: 'bg-green-100', text: 'text-green-600', border: 'border-green-300' },
  [LIFECYCLE_STATUS.OFFBOARDING]: { bg: 'bg-orange-100', text: 'text-orange-600', border: 'border-orange-300' },
  [LIFECYCLE_STATUS.OFFBOARDED]: { bg: 'bg-slate-200', text: 'text-slate-600', border: 'border-slate-300' },
};

// Property Status Types (for property record)
// Onboarding -> Onboarded -> Offboarding
export const PROPERTY_STATUS = {
  ONBOARDING: 'Onboarding',
  ONBOARDED: 'Onboarded',
  OFFBOARDING: 'Offboarding'
};

// NEW Onboarding Workflow Steps (6 steps as specified)
export const ONBOARDING_STEPS = [
  {
    id: 'agreement_documents',
    name: 'Agreement & Documents',
    description: 'Complete all agreements and collect required documents',
    order: 1
  },
  {
    id: 'photos_listing_form',
    name: 'Photos & Listing Form',
    description: 'Collect property photos and complete listing form',
    order: 2
  },
  {
    id: 'property_listing',
    name: 'Property Listing',
    description: 'List property on OTA platforms',
    order: 3,
    showOtaStatus: true  // Flag to show live OTAs
  },
  {
    id: 'quality_check',
    name: 'Quality Check (QC)',
    description: 'Verify all OTA listings are properly configured',
    order: 4,
    showOtaStatus: true  // Flag to show OTA names successfully listed
  },
  {
    id: 'rates_channel_setup',
    name: 'Rates & Channel Set Up',
    description: 'Configure rates and channel manager settings',
    order: 5
  },
  {
    id: 'post_live_review',
    name: 'Post Live Review',
    description: 'Final review after property goes live on all channels',
    order: 6
  }
];

// NEW Offboarding Workflow Steps (7 steps as specified)
// SECTION 4: Final Hand Over is editable by all users
export const OFFBOARDING_STEPS = [
  {
    id: 'request_initiated',
    name: 'Request Initiated',
    description: 'Offboarding request has been initiated',
    order: 1,
    hasReasonField: true,
    // RBAC mirror of backend OFFBOARDING_STEPS (server.py L10767+).
    // Frontend uses this for view/edit gating; backend re-checks on POST.
    allowedRoles: ['cx', 'cx_leader', 'gtm_sales', 'gtm_sales_leader', 'admin'],
  },
  {
    id: 'sales_retention',
    name: 'GTM Retention',
    description: 'GTM Sales team retention attempt — Offboard or Cancel.',
    order: 2,
    allowedRoles: ['gtm_sales', 'gtm_sales_leader', 'admin'],
    actions: [
      { value: 'offboard', label: 'Offboard', requiresReason: true, reasonLabel: 'Reason for offboarding' },
      { value: 'cancel', label: 'Cancel Offboarding', isCancel: true },
    ],
  },
  {
    id: 'ari_freeze',
    name: 'ARI Freeze',
    description: 'Freeze availability, rates, and inventory.',
    order: 3,
    allowedRoles: ['cx', 'cx_leader', 'admin'],
    actions: [
      { value: 'complete', label: 'Completed' },
      { value: 'pending', label: 'Pending', isNoop: true },
    ],
  },
  {
    id: 'finance_due',
    name: 'Finance',
    description: 'BFR commercial verification step.',
    order: 4,
    allowedRoles: ['bfr', 'admin'],
    actions: [
      { value: 'verified', label: 'Commercials checked and do the offboarding process' },
      { value: 'cancel', label: 'Cancel Offboarding', isCancel: true },
    ],
  },
  {
    id: 'cx_review',
    name: 'CX Review (remove our details)',
    description: 'Customer experience review and remove company details.',
    order: 5,
    allowedRoles: ['cx', 'cx_leader', 'admin'],
    actions: [
      { value: 'complete', label: 'Review Complete' },
    ],
  },
  {
    id: 'final_bfe_noc',
    name: 'NOC',
    description: 'Upload signed NOC document to complete this step.',
    order: 6,
    allowedRoles: ['cx', 'cx_leader', 'admin'],
    requiresUpload: true,
    actions: [
      { value: 'complete', label: 'NOC Complete', requiresUpload: true },
    ],
  },
  {
    id: 'final_hand_over',
    name: 'Final Handover',
    description: 'Executive sign-off for final handover.',
    order: 7,
    allowedRoles: ['executive', 'admin'],
    actions: [
      { value: 'complete', label: 'Do the Offboarding', isFinal: true },
      { value: 'cancel', label: 'Cancel Offboarding', isCancel: true },
    ],
  },
];

// Step Status Types
export const STEP_STATUS = {
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  SKIPPED: 'skipped'
};

export const STEP_STATUS_LABELS = {
  [STEP_STATUS.PENDING]: 'Pending',
  [STEP_STATUS.IN_PROGRESS]: 'In Progress',
  [STEP_STATUS.COMPLETED]: 'Completed',
  [STEP_STATUS.SKIPPED]: 'Skipped'
};

export const STEP_STATUS_COLORS = {
  [STEP_STATUS.PENDING]: { bg: 'bg-gray-100', text: 'text-gray-500', icon: 'text-gray-400', border: 'border-gray-200' },
  [STEP_STATUS.IN_PROGRESS]: { bg: 'bg-blue-100', text: 'text-blue-600', icon: 'text-blue-500', border: 'border-blue-200' },
  [STEP_STATUS.COMPLETED]: { bg: 'bg-green-100', text: 'text-green-600', icon: 'text-green-500', border: 'border-green-200' },
  [STEP_STATUS.SKIPPED]: { bg: 'bg-yellow-100', text: 'text-yellow-600', icon: 'text-yellow-500', border: 'border-yellow-200' }
};

// Activity Log Types
export const ACTIVITY_TYPES = {
  STATUS_CHANGE: 'status_change',
  STEP_COMPLETED: 'step_completed',
  STEP_STARTED: 'step_started',
  USER_ASSIGNED: 'user_assigned',
  NOTE_ADDED: 'note_added',
  DOCUMENT_UPLOADED: 'document_uploaded',
  FINAL_GO_LIVE: 'final_go_live',
  OFFBOARDING_STARTED: 'offboarding_started',
  OFFBOARDING_COMPLETED: 'offboarding_completed',
  LIFECYCLE_CREATED: 'lifecycle_created'
};

// Lead Stages
export const LEAD_STAGES = {
  NEW: 'NEW',
  CONTACTED: 'CONTACTED',
  QUALIFIED: 'QUALIFIED',
  NEGOTIATION: 'NEGOTIATION',
  CLOSED_WON: 'CLOSED WON',
  CLOSED_LOST: 'CLOSED LOST',
  DISCONTINUED: 'DISCONTINUED'
};
